import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLAriaBasics = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Accessibility (A11Y)</div>
                <h1>ARIA Roles & Attributes</h1>
                <p className="hero-lead">
                    Master ARIA (Accessible Rich Internet Applications) to make dynamic web content accessible.
                    Learn roles, states, properties, and when to use ARIA vs native HTML.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Internal Engine: The Accessibility Object Model (AOM)</h2>
                <p>
                    To understand ARIA, you must understand how a browser actually "sees" your page.
                    Just as the browser creates the <strong>DOM (Document Object Model)</strong> for
                    rendering and JavaScript, it simultaneously maintains the
                    <strong>Accessibility Tree</strong> (or <strong>AOM</strong>).
                </p>

                <ProTip><p><strong>Senior Engineering Insight:</strong> Assistive technologies (like
                        screen readers) don't actually read your HTML source code. They query the
                        <strong>AOM</strong>. ARIA is the API we use to manually modify that tree
                        when the default HTML mapping fails.</p></ProTip>

                <div className="diagram-box">
                    <CodeBlock language="text" code={`[Source HTML] -&gt; [Parsing Engine] -&gt; [DOM Tree]
                                            |
                                            v
                                   [Accessibility Tree]
                                            |
                                            v
                                   [Assistive Technology (NVDA/VoiceOver)]`} />
                </div>
            </div>

            <div className="content-section">
                <h2>2. The Philosophy of ARIA: A Bridge, Not a Crutch</h2>
                <p>
                    ARIA was created during the "Ajax transition" of the early 2000s, when developers
                    began building complex UI widgets (like tabs and sliders) using <code>&lt;div&gt;</code>
                    and <code>&lt;span&gt;</code>. These elements lacked the built-in semantics
                    needed for screen readers.
                </p>

                <div className="key-principle">
                    <p><strong>The Golden Rule: Semantic Parity.</strong> ARIA does not
                        <em>change</em> the behavior of an element. It only <em>communicates</em>
                        that behavior to the AOM. If you add <code>role="button"</code> to a
                        <code>div</code>, it still won't respond to the Enter key unless you write
                        the JavaScript for it.</p>
                </div>

                <ProTip><p className="highlight-text">
                        <strong>"If a native HTML element or attribute has the semantics and behavior
                            you require, use that instead of ARIA."</strong>
                    </p>
                    <p>
                        Browsers have spent decades optimizing the accessibility of native
                        <code>&lt;button&gt;</code> and <code>&lt;select&gt;</code> elements.
                        Your custom ARIA implementation is almost certainly less robust than the browser's
                        native implementation.
                    </p></ProTip>
            </div>

            <div className="content-section">
                <h2>What is ARIA?</h2>
                <p>
                    <strong>ARIA (Accessible Rich Internet Applications)</strong> provides additional semantics
                    for assistive technologies when HTML alone isn't sufficient. It's especially important for
                    dynamic, JavaScript-heavy applications.
                </p>
            </div>

            <div className="content-section">
                <h2>3. The ARIA Role Hierarchy</h2>
                <p>
                    Roles define <strong>what</strong> an element is. The ARIA specification organizes
                    roles into a hierarchy, where sub-roles inherit properties from their parent roles.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>Landmark Roles</h4>
                        <p>Regions of the page that allow users to jump focus (e.g., <code>main</code>, <code>nav</code>).</p>
                    </div>
                    <div className="concept-card">
                        <h4>Widget Roles</h4>
                        <p>Standalone interactive UI components (e.g., <code>button</code>, <code>slider</code>, <code>dialog</code>).</p>
                    </div>
                    <div className="concept-card">
                        <h4>Structure Roles</h4>
                        <p>Organizational containers that define document layout (e.g., <code>list</code>, <code>table</code>, <code>row</code>).</p>
                    </div>
                </div>

                <h3>Case Study: The Accessible Accordion</h3>
                <p>
                    Accordions are a classic example of where <code>ARIA</code> provides the state
                    necessary for a screen reader to understand hidden content.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Production-Ready Accordion Blueprint</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- The Toggle Header --&gt;
<h3 id="billing-header">
    <button aria-expanded="false" 
            aria-controls="billing-panel"
            class="accordion-trigger">
        Billing Information
        <span class="icon" aria-hidden="true"></span>
    </button>
</h3>

<!-- The Content Panel --&gt;
<div id="billing-panel" 
     role="region" 
     aria-labelledby="billing-header" 
     hidden 
     class="accordion-panel">
    <p>Please enter your payment methods and invoice history here.</p>
</div>`} />
                </div>
                <ProTip><p><strong>Senior Checklist:</strong> In the example above, the <code>aria-controls</code>
                        creates a <strong>technical link</strong> between the trigger and the content. When
                        the user clicks the trigger, your JS should flip <code>aria-expanded</code> to
                        <code>true</code> and toggle the <code>hidden</code> attribute.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>4. ARIA States and Properties: The Vocabulary of Interaction</h2>
                <p>
                    While roles define <strong>what</strong> an element is, states and properties define
                    <strong>how</strong> it is currently behaving. States (like <code>aria-checked</code>)
                    change frequently via JavaScript, whereas properties (like <code>aria-labelledby</code>)
                    tend to be static.
                </p>

                <h3>1. Naming & Relationships (The 'Label' Trio)</h3>
                <p>
                    Correctly naming your elements is the difference between a screen reader saying
                    "Button" and "Submit Payment Button."
                </p>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                <th>Function</th>
                                <th>Visual Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>aria-label</code></td>
                                <td>Directly strings text to an element.</td>
                                <td>Invisible to sighted users.</td>
                            </tr>
                            <tr>
                                <td><code>aria-labelledby</code></td>
                                <td>References another element's <code>ID</code>.</td>
                                <td>Uses an existing visible label.</td>
                            </tr>
                            <tr>
                                <td><code>aria-describedby</code></td>
                                <td>Provides supplementary info (hint text).</td>
                                <td>Announced after the name.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3>Case Study: The Accessible Tab Interface</h3>
                <p>
                    Tab interfaces are notoriously difficult to get right. They require a
                    coordinated dance between roles, states, and keyboard focus.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>The W3C ARIA Tab Pattern</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- 1. The Container --&gt;
<div role="tablist" aria-label="Entertainment Settings">
    <!-- 2. The Triggers --&gt;
    <button role="tab" 
            aria-selected="true" 
            aria-controls="panel-video" 
            id="tab-video">Video</button>
    <button role="tab" 
            aria-selected="false" 
            aria-controls="panel-audio" 
            id="tab-audio" 
            tabindex="-1">Audio</button>
</div>

<!-- 3. The Panels --&gt;
<div role="tabpanel" 
     id="panel-video" 
     aria-labelledby="tab-video">
    [Video settings content...]
</div>
<div role="tabpanel" 
     id="panel-audio" 
     aria-labelledby="tab-audio" 
     hidden>
    [Audio settings content...]
</div>`} />
                </div>

                <ProTip><p><strong>Senior Strategy: The tabindex="-1" trick.</strong> Notice that the inactive
                        tab has <code>tabindex="-1"</code>. This is part of the <strong>Roving Tabindex</strong>
                        pattern. Only the active tab should be in the natural tab order. The user then uses
                        <strong>Arrow Keys</strong> to switch between tabs within the list.</p></ProTip>
            </div>
            <div className="content-section">
                <h2>ARIA States and Properties</h2>

                <h3>aria-label</h3>
                <p>Provides accessible name when visible text isn't suitable:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button aria-label="Close dialog">
    <span aria-hidden="true"></span>
</button>

<nav aria-label="Primary navigation">
    <!-- Navigation links --&gt;
</nav>

<!-- Use when: --&gt;
<!-- - Icon-only buttons --&gt;
<!-- - Multiple similar elements need distinction --&gt;
<!-- - Visual label isn't descriptive enough --&gt;`} />
                </div>

                <h3>aria-labelledby and aria-describedby</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- aria-labelledby: Links to element that labels this one --&gt;
<div id="dialog-title">Confirm Delete</div>
<div role="dialog" aria-labelledby="dialog-title">
    <p>Are you sure?</p>
    <button>Delete</button>
    <button>Cancel</button>
</div>

<!-- aria-describedby: Links to element that describes this one --&gt;
<input type="password" 
       aria-describedby="password-requirements">
<div id="password-requirements">
    Must be 8+ characters with numbers and letters
</div>`} />
                </div>

                <h3>aria-hidden</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Hide decorative content from screen readers --&gt;
<button>
    Save
    <span aria-hidden="true"></span>
</button>

<!-- Hide duplicate content --&gt;
<div aria-hidden="true"> Back</div>
<a href="/back">Back</a>

<!--  Warning: Never use aria-hidden on focusable elements! --&gt;
<button aria-hidden="true">Click me</button> <!--  Bad! --&gt;`} />
                </div>

                <h3>aria-expanded</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button aria-expanded="false" aria-controls="menu">
    Menu
</button>

<div id="menu" hidden>
    <!-- Menu items --&gt;
</div>

<script>
button.addEventListener('click', () => {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', !expanded);
    menu.hidden = expanded;
});
</script>

<!-- Common with: dropdowns, accordions, collapsible sections --&gt;`} />
                </div>

                <h3>aria-current</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Indicate current page in navigation --&gt;
<nav>
    <a href="/">Home</a>
    <a href="/about">About</a>
    <a href="/blog" aria-current="page">Blog</a>
    <a href="/contact">Contact</a>
</nav>

<!-- Values: page, step, location, date, time, true, false --&gt;`} />
                </div>

                <div className="content-section">
                    <h2>5. The Science of ARIA Live Regions</h2>
                    <p>
                        In a single-page application (SPA), content changes without a page reload. Screen
                        readers need to be proactively "notified" of these changes. This is handled by
                        <strong>Live Regions</strong>.
                    </p>

                    <div className="comparison-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Value</th>
                                    <th>Interrupt Strategy</th>
                                    <th>Best Use Case</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>off</code></td>
                                    <td>None (Silent)</td>
                                    <td>Static content or non-critical updates.</td>
                                </tr>
                                <tr>
                                    <td><code>polite</code></td>
                                    <td>Waits for user to stop typing/navigating.</td>
                                    <td>Success messages, minor status updates.</td>
                                </tr>
                                <tr>
                                    <td><code>assertive</code></td>
                                    <td>Interrupts current speech immediately.</td>
                                    <td>Security warnings, critical errors, timers.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <h3>Communicating Granular Changes</h3>
                    <p>
                        Sometimes you don't want to announce the *entire* container, just the part that
                        changed. This is where <code>aria-atomic</code> and <code>aria-relevant</code> come in.
                    </p>
                    <div className="code-example">
                        <div className="code-header">
                            <span>Dynamic Score Tracker Example</span>
                        </div>
                        <CodeBlock language="html" code={`<div aria-live="polite" 
     aria-atomic="true" 
     aria-relevant="all">
    <h3>Current Score</h3>
    <p>Player 1: <span id="p1-score">12</span></p>
    <p>Player 2: <span id="p2-score">8</span></p>
</div>`} />
                    </div>
                    <ProTip><p><strong>Senior Detail: aria-atomic="true".</strong> By default, some screen readers
                            only announce the changed text. By setting <code>aria-atomic="true"</code>, you force
                            the reader to announce the "Context" (e.g., "Current Score, Player 1: 13") rather
                            than just saying "13".</p></ProTip>
                </div>

                <div className="content-section">
                    <h2>6. Pattern Case Study: The Custom Toggle Switch</h2>
                    <p>
                        A toggle switch is common in modern UI but doesn't exist natively in HTML5 (which
                        uses checkboxes). To build a premium toggle, you must combine specific ARIA
                        roles and keyboard logic.
                    </p>

                    <div className="code-example">
                        <div className="code-header">
                            <span>High-Efficiency Toggle Component</span>
                        </div>
                        <CodeBlock language="html" code={`<div class="toggle-container">
    <span id="label-darkmode">Dark Mode</span>
    <button type="button" 
            role="switch" 
            aria-checked="false" 
            aria-labelledby="label-darkmode"
            class="premium-switch">
        <span class="switch-handle" aria-hidden="true"></span>
    </button>
</div>`} />
                    </div>

                    <ProTip><p><strong>Why role="switch"?</strong> While <code>role="checkbox"</code> works,
                            <strong>role="switch"</strong> signals to the user that the action is
                            <strong>immediate</strong> (like a light switch) rather than requiring a
                            submit button click.</p></ProTip>
                </div>
                <h3>aria-invalid and aria-errormessage</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<label for="email">Email:</label>
<input type="email" 
       id="email" 
       aria-invalid="true"
       aria-describedby="email-error">
<span id="email-error" role="alert">
    Please enter a valid email address
</span>

<script>
input.addEventListener('blur', () => {
    if (!input.validity.valid) {
        input.setAttribute('aria-invalid', 'true');
    } else {
        input.setAttribute('aria-invalid', 'false');
    }
});
</script>`} />
                </div>

                <h3>aria-disabled vs disabled</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Native disabled: Not focusable, not submittable --&gt;
<button disabled>Save</button>

<!-- aria-disabled: Focusable but indicates disabled state --&gt;
<button aria-disabled="true">Save</button>

<script>
// Must handle disabled state manually
button.addEventListener('click', (e) => {
    if (button.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
    }
    // Handle click
});
</script>

<!-- Use aria-disabled when: --&gt;
<!-- - Need element to remain in tab order --&gt;
<!-- - Need to explain why it's disabled --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>9. The "Accessible Name" Computation Algorithm</h2>
                <p>
                    When a screen reader encounters an element, it follows a specific precedence to
                    calculate its name. Understanding this "Waterfall" is crucial for debugging.
                </p>

                <ProTip><ol>
                        <li><strong>aria-labelledby:</strong> If this exists, it wins. All other names are ignored.</li>
                        <li><strong>aria-label:</strong> Used if <code>aria-labelledby</code> is missing.</li>
                        <li><strong>Native Label/Alt:</strong> The <code>&lt;label&gt;</code> for inputs or <code>alt</code> for images.</li>
                        <li><strong>Inner Content:</strong> The text between tags (e.g., <code>&lt;button&gt;Click Me&lt;/button&gt;</code>).</li>
                        <li><strong>Title Attribute:</strong> The absolute last resort (often ignored by many readers).</li>
                    </ol></ProTip>
            </div>

            <div className="content-section">
                <h2>10. Diagnosing "Aria-itis": Common Mistakes in Production</h2>
                <p>
                    "Aria-itis" refers to the tendency of developers to add ARIA to everything in an
                    attempt to be helpful, which actually makes the experience worse.
                </p>

                <div className="mistakes-list">
                    <div className="mistake-card">
                        <h3> Using aria-label on non-interactive elements</h3>
                        <p>Adding <code>aria-label="Important stuff"</code> to a <code>&lt;div&gt;</code>
                            often does nothing. Screen readers only announce labels for
                            <strong>Landmarks</strong>, <strong>Widgets</strong>, or <strong>Links</strong>.</p>
                    </div>
                    <div className="mistake-card">
                        <h3> Conflicting Roles</h3>
                        <p>Example: <code>&lt;button role="heading"&gt;</code>. This is a semantic
                            contradiction. Is it a button you can click or a header for a section?
                            Screen readers will struggle to categorize this.</p>
                    </div>
                    <div className="mistake-card">
                        <h3> Leaving ARIA behind</h3>
                        <p>If you update your UI with JS but don't flip the <code>aria-expanded</code>
                            or <code>aria-checked</code> state, you have created a "Deceptive UI" for
                            low-vision users.</p>
                    </div>
                </div>
            </div>
            <div className="content-section">
                <h2>Common ARIA Patterns</h2>

                <h3>Modal Dialog</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div role="dialog" 
     aria-labelledby="dialog-title"
     aria-modal="true">
    
    <h2 id="dialog-title">Confirm Action</h2>
    <p>Are you sure you want to proceed?</p>
    
    <button>Confirm</button>
    <button>Cancel</button>
</div>

<!-- aria-modal="true" tells screen readers this is modal --&gt;
<!-- Focus should be trapped inside dialog --&gt;
<!-- Escape key should close dialog --&gt;`} />
                </div>

                <h3>Alert</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Automatically announced --&gt;
<div role="alert">
    <p>Your session will expire in 5 minutes</p>
</div>

<!-- Or with aria-live --&gt;
<div aria-live="assertive" aria-atomic="true">
    <p>Error: Form submission failed</p>
</div>`} />
                </div>

                <h3>Progress Bar</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div role="progressbar" 
     aria-valuenow="45" 
     aria-valuemin="0" 
     aria-valuemax="100"
     aria-label="Upload progress">
    <div style="width: 45%">45%</div>
</div>

<!-- Better: Use native progress element --&gt;
<progress value="45" max="100">45%</progress>`} />
                </div>

                <h3>Custom Checkbox</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div role="checkbox" 
     aria-checked="false" 
     tabindex="0"
     aria-labelledby="checkbox-label">
    <span aria-hidden="true"></span>
</div>
<span id="checkbox-label">Accept terms</span>

<script>
checkbox.addEventListener('click', () => {
    const checked = checkbox.getAttribute('aria-checked') === 'true';
    checkbox.setAttribute('aria-checked', !checked);
    checkbox.querySelector('span').textContent = !checked ? '' : '';
});

// Handle keyboard (Space/Enter)
checkbox.addEventListener('keydown', (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        checkbox.click();
    }
});
</script>

<!-- Much better: Use native checkbox! --&gt;
<input type="checkbox" id="terms">
<label for="terms">Accept terms</label>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. The Five Rules of ARIA: A Senior Developer's Guide</h2>
                <p>
                    Understanding the "Why" behind ARIA requires adhering to the W3C's five core rules.
                    These are the benchmarks for production-grade accessibility.
                </p>

                <ProTip><ol>
                        <li><strong>Rule 1: Don't use ARIA.</strong> As discussed, native HTML is always superior.</li>
                        <li><strong>Rule 2: Don't change native semantics.</strong> Never add <code>role="heading"</code>
                            to a <code>&lt;button&gt;</code>. If it's a button, keep it as a button.</li>
                        <li><strong>Rule 3: All ARIA widgets must be keyboard accessible.</strong> If you
                            build a custom widget, you are responsible for <code>tabindex</code> and
                            event listeners.</li>
                        <li><strong>Rule 4: Don't use role="presentation" or aria-hidden="true" on focusable
                            elements.</strong> This creates "ghost" elements that are clickable but
                            invisible to screen readers.</li>
                        <li><strong>Rule 5: All interactive elements must have an accessible name.</strong>
                            A button with no text or <code>aria-label</code> is a failure.</li>
                    </ol></ProTip>
            </div>

            <div className="content-section">
                <h2>8. The Professional Screen Reader Testing Protocol</h2>
                <p>
                    Automated tools (like Lighthouse) only catch about 30-40% of accessibility issues.
                    To build Super-Premium content, you must perform manual audits using the
                    "No Mouse" challenge.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Test Element</th>
                                <th>Checkmark</th>
                                <th>Success Criteria</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Focus Order</td>
                                <td>[ ]</td>
                                <td>Focus moves logically (top-to-bottom, left-to-right).</td>
                            </tr>
                            <tr>
                                <td>Visible Outlines</td>
                                <td>[ ]</td>
                                <td>Every interactive element has a clear, high-contrast focus ring.</td>
                            </tr>
                            <tr>
                                <td>Announcements</td>
                                <td>[ ]</td>
                                <td>Dynamic content updates (live regions) are spoken by the reader.</td>
                            </tr>
                            <tr>
                                <td>Hidden Content</td>
                                <td>[ ]</td>
                                <td>Content that is visually hidden is truly hidden from the AOM.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><p><strong>Professional Tip:</strong> When testing with <strong>VoiceOver</strong>
                        on a Mac, use <code>Command + F5</code> to toggle the screen reader. Use
                        <code>Control + Option + U</code> to open the Rotor and verify that your
                        ARIA landmarks and roles are correctly listed.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>11. Accessibility in the Era of Web Components (Shadow DOM)</h2>
                <p>
                    Modern frameworks often use the <strong>Shadow DOM</strong> for encapsulation.
                    This creates a unique challenge for ARIA because <strong>IDs do not cross
                        Shadow boundaries</strong>. This means an <code>aria-labelledby</code> in
                    the Light DOM cannot reference an element inside a Shadow Root.
                </p>

                <ProTip><p><strong>Senior Engineering Insight:</strong> To solve the "ID Gap," developers
                        are increasingly turning to the <strong>Accessibility Object Model (AOM) API</strong>
                        (currently in draft), which allows setting ARIA properties directly on the node
                        object without relying on ID strings.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>AOM API Example (Experimental)</span>
                    </div>
                    <CodeBlock language="javascript" code={`// Setting accessible properties directly on the element
const myButton = document.querySelector('custom-element').shadowRoot.querySelector('button');
myButton.ariaLabel = "Global Search";
myButton.ariaExpanded = "false";`} />
                </div>
            </div>

            <div className="content-section">
                <h2>12. The Evolution: WAI-ARIA 1.0 to 1.3</h2>
                <p>
                    ARIA is a living standard. Understanding its evolution helps you build
                    future-proof applications.
                </p>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Version</th>
                                <th>Major Focus</th>
                                <th>Key Addition</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>1.0</td>
                                <td>Static Roles</td>
                                <td>Landmarks and basic widgets.</td>
                            </tr>
                            <tr>
                                <td>1.1</td>
                                <td>Interactive States</td>
                                <td><code>aria-expanded</code> and <code>aria-current</code>.</td>
                            </tr>
                            <tr>
                                <td>1.2+</td>
                                <td>Complex Semantic Parity</td>
                                <td><code>role="combobox"</code> and deeper keyboard requirements.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p>
                    The <strong>1.3 draft</strong> focuses heavily on "Intent-based Accessibility,"
                    moving away from specific roles towards "Intent" mapping, which helps
                    assistive technology understand the <strong>Goal</strong> of the user
                    rather than just the <strong>Appearance</strong> of the component.
                </p>
            </div>

            <div className="content-section">
                <h2>14. The Future: AOM and Semantic Interoperability</h2>
                <p>
                    The next frontier of web accessibility is the <strong>Accessibility Object
                        Model (AOM)</strong>. This initiative aims to provide developers with a
                    programmatic way to modify the accessibility tree without needing to
                    "leak" implementation details into the DOM via ARIA attributes.
                </p>
                <ProTip><p><strong>The Goal:</strong> Imagine being able to tell the browser
                        "this object is a button" directly in your JavaScript constructor,
                        ensuring that accessibility is baked into the component's logic rather
                        than being an afterthought added to the HTML template.</p></ProTip>
                <p>
                    As we move towards more complex, AR-VR enabled web experiences (WebXR),
                    ARIA's role will expand to include spatial relationships and 3D
                    interaction states, ensuring that "Hypertext" remains accessible no
                    matter what the medium.
                </p>
            </div>

            <div className="content-section">
                <h2>15. Conclusion: The Inclusive Engineer's Mindset</h2>
                <p>
                    ARIA is more than just a list of attributes; it's a commitment to
                    <strong>Universal Design</strong>. A senior engineer doesn't just ask
                    "Does it work?" but "Who might this exclude?". By mastering the
                    Accessibility Object Model and the nuanced vocabulary of ARIA, you
                    ensure that the web remains a platform for everyone.
                </p>
            </div>
        </div>
    );
};

export default HTMLAriaBasics;



